import React, { useState } from "react";
import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import { Icon, type IconProp } from "@proda-ai/ui-kit";
import Typography from "../Typography";

export type CollapsedTextProps = BoxProps & {
    text: string;
    icon?: IconProp;
    minWidth?: number;
    maxWidth?: number;
    expanded?: boolean;
};

export const testId = "CollapsedText";

/**
 * CollapsedText component that displays text with optional icon and expandable behavior.
 *
 * @param text - The text to display
 * @param icon - Optional icon to display before the text
 * @param minWidth - Minimum width when collapsed (default: 0)
 * @param maxWidth - Maximum width when expanded (default: 500)
 * @param expanded - Whether the text is expanded (default: false)
 */
const CollapsedText = (props: CollapsedTextProps) => {
    const {
        sx,
        className,
        icon,
        minWidth = 0,
        maxWidth = 500,
        text,
        expanded = false,
        ...otherProps
    } = props;

    const [isHovered, setIsHovered] = useState(false);

    return (
        <Box
            data-testid={testId}
            className={clsx([testId, className])}
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                ...sx,
            }}
            onMouseEnter={() => {
                setIsHovered(true);
            }}
            onMouseLeave={() => {
                setIsHovered(false);
            }}
            onFocus={() => {
                setIsHovered(true);
            }}
            onBlur={() => {
                setIsHovered(false);
            }}
            {...otherProps}
        >
            {icon !== undefined && (
                <Typography variant="body3">
                    <Icon icon={icon} />
                </Typography>
            )}
            <Box
                sx={{
                    maxWidth:
                        isHovered || expanded
                            ? `${maxWidth}px`
                            : `${minWidth}px`,
                    overflow: "hidden",
                    textOverflow: "hidden",
                    whiteSpace: "nowrap",
                    transition: "max-width 0.2s ease",
                    marginLeft: icon !== undefined ? "4px" : 0,
                }}
            >
                <Typography
                    variant="body2"
                    component="a"
                    sx={{
                        color: "inherit",
                        textDecoration: "none",
                        fontFamily:
                            "DM Sans, arimo, arial, helvetica, 'Helvetica Neue', -apple-system, sans-serif",
                        "&:hover": {
                            textDecoration: "underline",
                        },
                    }}
                >
                    {text}
                </Typography>
            </Box>
        </Box>
    );
};

export default CollapsedText;
